"use client"

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react"
import { useRouter } from "next/navigation"
import { MultiSelect } from "@signalmax/ui"
import { CareersHeader } from "@/components/max/CareersHeader"
import { CareersFooter } from "@/components/max/CareersFooter"
import { CAREERS_MAX_W, CAREERS_INSET } from "@/lib/career/layout"
import { trackCareer } from "@/lib/max/analytics"
import { useCareerPricing } from "@/components/max/useCareerPricing"
import { LaunchBadge } from "@/components/max/LaunchBadge"
import { INDUSTRIES, LOCATIONS, ROLE_TITLES } from "@/lib/career/referenceData"
import { MarketingResponsiveStyle, MKT_MOBILE_ONLY, DesktopOnly } from "@/components/max/marketing/Responsive"

// ────────────────────────────────────────────────────────────────────────────
// /max/try : the PUBLIC Career Intelligence tester (careers.signalmax.ai/try).
//
// Acquisition front door for TikTok / IG. NO email or account needed to test:
// target titles + locations + graduation year give real eligible internships
// (deterministic Phase 5 eligibility engine, no LLM). Shows ~5 real roles + the
// total eligible count, then "unlock all, ranked by fit" via free signup.
//
// Restyled to the @signalmax/ui "Careers Try" hi-fi handoff: same shell as the
// landing (sticky header, footer, tokens), a two-column layout (form left, a
// sticky "what you'll see next" aside right that previews the payoff). All the
// real behavior (résumé autofill, teaser submit, results view, cities select) is
// preserved; only layout + styling changed. Public, theme-aware.
// ────────────────────────────────────────────────────────────────────────────

interface Sample {
  company_name: string | null
  role_title: string
  career_track: string | null
  location: string | null
  term_season: string | null
  term_year: number | null
  description: string | null
  apply_url: string | null
}
interface Teaser {
  graduation_year: number | null
  titles: string[]
  locations: string[]
  total_active: number
  eligible_count: number
  samples: Sample[]
  sample_locked_count: number
}
// Résumé parse result awaiting the visitor's review before it fills the form.
type ParsedProfile = { graduation_year: number | null; titles: string[]; locations: string[]; major: string | null; university: string | null; skills: string[]; career_tracks?: string[] }

const GRAD_YEARS = [2026, 2027, 2028, 2029, 2030, 2031]
const TERMS = [
  { v: "summer_2027", label: "Summer 2027" },
  { v: "fall_2027", label: "Fall 2027" },
  { v: "spring_2028", label: "Spring 2028" },
  { v: "summer_2028", label: "Summer 2028" },
  { v: "", label: "Any term" },
]
const TITLE_SUGGESTIONS = [
  "Software Engineer Intern", "Data Analyst Intern", "Marketing Intern", "Finance Intern",
  "Accounting Intern", "Consulting Analyst Intern", "Mechanical Engineering Intern", "Product Manager Intern",
]
const INSET = CAREERS_INSET

// 50px tall with 16px text: below 16px iOS Safari zooms the viewport on focus, which breaks the
// layout mid-entry (mobile-first funnel rule).
const field: CSSProperties = { boxSizing: "border-box", width: "100%", minHeight: 50, padding: "0 12px", fontSize: "var(--type-base)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface-sunken, var(--surface))", color: "var(--text-primary)", outline: "none" }
const optionalTag = <span style={{ fontWeight: 500, color: "var(--text-muted)" }}> (optional)</span>

function yearBtn(active: boolean): CSSProperties {
  return {
    minHeight: 48,
    padding: "0 var(--space-3)",
    fontSize: "var(--type-base)",
    fontWeight: 600,
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    background: active ? "var(--accent)" : "transparent",
    color: active ? "var(--on-accent, #fff)" : "var(--text-primary)",
    border: `1px solid ${active ? "var(--accent)" : "var(--border-strong)"}`,
  }
}

function Donut({ pct, size, label }: { pct: number; size: number; label: string }) {
  const stroke = Math.max(5, Math.round(size / 11))
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const cx = size / 2
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${pct} percent ${label}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--border-subtle)" strokeWidth={stroke} />
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--accent)" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${(circ * pct) / 100} ${circ}`} transform={`rotate(-90 ${cx} ${cx})`} />
      <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" fontFamily="var(--font-display)" fontWeight={700} fontSize={Math.round(size * 0.3)} fill="var(--text-primary)">{pct}</text>
      <text x="50%" y="67%" textAnchor="middle" dominantBaseline="middle" fontFamily="var(--font-mono)" fontSize={Math.max(9, Math.round(size * 0.13))} letterSpacing="0.04em" fill="var(--text-muted)">{label}</text>
    </svg>
  )
}

export default function TryClient() {
  const router = useRouter()
  const pricing = useCareerPricing()
  // Default to the class the Summer 2027 season actually targets: current juniors, who
  // graduate 2028 and do their junior-summer internship that year (2027 graduates are
  // seniors interning the prior summer, the smallest cohort). Overridden by the visitor's
  // saved search or a parsed resume the moment either provides a year.
  const [gradYear, setGradYear] = useState<number | "">(2028)
  const [titles, setTitles] = useState<string[]>([])
  const [titleDraft, setTitleDraft] = useState("")
  const [locations, setLocations] = useState<string[]>([])
  const [industries, setIndustries] = useState<string[]>([])
  const [term, setTerm] = useState("summer_2027")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [coverage, setCoverage] = useState<number | null>(null)
  // Carried silently to improve eligibility + seed a future profile (no visible field here).
  const [major, setMajor] = useState("")
  const [university, setUniversity] = useState("")
  const [skills, setSkills] = useState<string[]>([])
  const [resumeBusy, setResumeBusy] = useState(false)
  const [resumeStage, setResumeStage] = useState(0) // 0 reading, 1 extracting (processing animation)
  const [resumeNote, setResumeNote] = useState<string | null>(null)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedProfile | null>(null) // awaiting review, not yet applied
  const [resumeId, setResumeId] = useState<string | null>(null) // stored teaser résumé, to carry into the account on signup
  const [resumeTracks, setResumeTracks] = useState<string[]>([]) // résumé-inferred career tracks — ranks the sample by fit
  // Three ways to bring in a résumé — same as the setup wizard: upload a file, paste the text,
  // or link to it. All land on the same parse endpoint + review card.
  const [intakeMode, setIntakeMode] = useState<"file" | "paste" | "link">("file")
  const [pasteText, setPasteText] = useState("")
  const [linkUrl, setLinkUrl] = useState("")
  const [autoSubmit, setAutoSubmit] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  // Real live coverage count for the "N internships tracked" line (best-effort, non-blocking).
  useEffect(() => {
    let active = true
    fetch("/api/career/coverage")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (active && j && j.ok && typeof j.internships_tracked === "number") setCoverage(j.internships_tracked) })
      .catch(() => {})
    return () => { active = false }
  }, [])

  // Pre-fill the tester from the visitor's last search (no account needed to look).
  useEffect(() => {
    try {
      const saved = localStorage.getItem("career_teaser_state")
      if (!saved) return
      const s = JSON.parse(saved) as {
        graduation_year?: number; desired_titles?: string[]; desired_locations?: string[]; desired_industries?: string[]
        internship_term?: string; major?: string; university?: string; skills?: string[]
      }
      if (typeof s.graduation_year === "number") setGradYear(s.graduation_year)
      if (Array.isArray(s.desired_titles)) setTitles(s.desired_titles)
      if (Array.isArray(s.desired_locations)) setLocations(s.desired_locations)
      if (Array.isArray(s.desired_industries)) setIndustries(s.desired_industries)
      if (typeof s.internship_term === "string") setTerm(s.internship_term)
      if (typeof s.major === "string") setMajor(s.major)
      if (typeof s.university === "string") setUniversity(s.university)
      if (Array.isArray(s.skills)) setSkills(s.skills)
    } catch { /* private mode / bad json: ignore */ }
  }, [])

  // Apply a reviewed parse to the form fields. Only fills what the résumé actually stated.
  const applyParsed = (p: ParsedProfile) => {
    if (typeof p.graduation_year === "number") setGradYear(p.graduation_year)
    if (p.titles?.length) setTitles(p.titles)
    if (p.locations?.length) setLocations(p.locations)
    if (p.major) setMajor(p.major)
    if (p.university) setUniversity(p.university)
    if (p.skills?.length) setSkills(p.skills)
  }

  // Shared intake → staged processing → hold the parse for review (never auto-applied). The
  // visitor confirms in the review card, mirroring the app's parse-then-confirm import. `run`
  // performs the POST (file, paste, or link) and returns the fetch Response.
  const runResumeIntake = async (label: string, badFileMsg: string, run: () => Promise<Response>) => {
    setResumeBusy(true); setResumeStage(0); setResumeError(null); setResumeNote(null); setParsed(null)
    const stageTimer = setTimeout(() => setResumeStage(1), 1100) // advance "reading" → "extracting"
    try {
      const res = await run()
      const json = await res.json().catch(() => ({}))
      if (!json?.ok) throw new Error(json?.error?.message || badFileMsg)
      const p = json.profile as ParsedProfile
      const anything = (typeof p.graduation_year === "number") || p.titles?.length || p.locations?.length || p.major || p.university || p.skills?.length
      if (!anything) { setResumeError("We read your résumé but couldn't pull details from it. Fill the form in below and you're good to go."); return }
      setParsed(p)
      setResumeId(typeof json.resume_id === "string" ? json.resume_id : null)
      setResumeTracks(Array.isArray(p.career_tracks) ? p.career_tracks : [])
      // Pre-fill the editable controls from what we read, so the review card and the form
      // below agree (the grad-year chip was staying on its default while the card
      // showed the parsed year). "Nothing is saved — you can change any of this below."
      applyParsed(p)
      void trackCareer("tester_resume_parsed", { from: label, titles: p.titles?.length || 0, locations: p.locations?.length || 0 })
    } catch (e) {
      setResumeError(e instanceof Error ? e.message : badFileMsg)
    } finally {
      clearTimeout(stageTimer)
      setResumeBusy(false)
      if (fileInput.current) fileInput.current.value = ""
    }
  }

  const uploadResume = (file: File) =>
    runResumeIntake("file", "We couldn't read that file. Try another, or fill the form in below.", () => {
      const fd = new FormData(); fd.append("file", file)
      return fetch("/api/career/teaser/parse-resume", { method: "POST", body: fd })
    })

  const submitResumeText = () => {
    const text = pasteText.trim()
    if (text.length < 40) { setResumeError("Paste a bit more of your résumé (at least a few lines)."); return }
    void runResumeIntake("paste", "We couldn't read that text. Try again, or fill the form in below.", () =>
      fetch("/api/career/teaser/parse-resume", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ paste_text: text }) }))
  }

  const submitResumeLink = () => {
    const url = linkUrl.trim()
    if (!/^https?:\/\/.+/i.test(url)) { setResumeError("Enter a public http(s) link to your résumé (PDF, DOCX, or TXT)."); return }
    void runResumeIntake("link", "We couldn't read that link. Try a direct PDF/DOCX link, or fill the form in below.", () =>
      fetch("/api/career/teaser/parse-resume", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ resume_url: url }) }))
  }

  // "Looks right" applies the parse and then runs the tester with the applied values
  // (state updates flush first, so this effect submits once the fields are set).
  useEffect(() => {
    if (!autoSubmit) return
    setAutoSubmit(false)
    void submit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit])

  // Fire career_test_started once, on the visitor's first real engagement with the tester.
  const startedRef = useRef(false)
  const markStarted = () => { if (!startedRef.current) { startedRef.current = true; void trackCareer("test_started", {}) } }
  const pickGradYear = (y: number) => { markStarted(); setGradYear(y) }

  const addTitle = (raw: string) => {
    markStarted()
    const t = raw.trim().replace(/,$/, "").trim()
    if (t && !titles.some((x) => x.toLowerCase() === t.toLowerCase())) setTitles((cur) => [...cur, t])
    setTitleDraft("")
  }
  const onTitleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTitle(titleDraft) }
    else if (e.key === "Backspace" && !titleDraft && titles.length) setTitles((cur) => cur.slice(0, -1))
  }
  const canSubmit = gradYear !== ""

  const entryMeta = useMemo(() => {
    if (typeof window === "undefined") return { referrer: null as string | null, entry_path: null as string | null }
    return { referrer: document.referrer || null, entry_path: window.location.pathname + window.location.search }
  }, [])

  const submit = useCallback(async () => {
    setBusy(true); setError(null)
    const finalTitles = titleDraft.trim() ? [...titles, titleDraft.trim()] : titles
    try {
      const res = await fetch("/api/career/teaser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ graduation_year: gradYear, titles: finalTitles, industries, locations, term, major: major || undefined, university: university || undefined, teaser_resume_id: resumeId || undefined, fit_tracks: resumeTracks, skills, ...entryMeta }),
      })
      const json = await res.json().catch(() => ({}))
      if (!json?.ok) throw new Error(json?.error?.message || "Something went wrong. Try again.")
      const t = json.teaser as Teaser
      const evt = { graduation_year: gradYear, titles: finalTitles.length, locations: locations.length, eligible_count: t.eligible_count, promo_active: pricing.promoActive, price_cents: pricing.effective.cents }
      void trackCareer("test_completed", evt)
      // Preserve the search AND the result so /sample renders without a re-run and
      // signup (no email needed to test) can seed the new profile.
      try {
        localStorage.setItem("career_teaser_state", JSON.stringify({
          graduation_year: gradYear, desired_titles: finalTitles, desired_locations: locations, desired_industries: industries, internship_term: term,
          major: major || undefined, university: university || undefined, skills: skills.length ? skills : undefined,
          eligible_count: t.eligible_count,
        }))
        localStorage.setItem("career_teaser_result", JSON.stringify(t))
      } catch { /* private mode / storage disabled: non-fatal */ }
      // Results live on the dedicated /sample paywall page per the redesign.
      router.push("/sample")
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Try again.")
    } finally {
      setBusy(false)
    }
  }, [gradYear, titles, titleDraft, industries, locations, term, major, university, skills, resumeId, resumeTracks, entryMeta, router])

  return (
    <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text-primary)", fontSize: "var(--type-base)", paddingBottom: "max(96px, calc(env(safe-area-inset-bottom) + 84px))" }}>
      {/* Sticky top bar (shared marketing header container) */}
      <MarketingResponsiveStyle />
      <CareersHeader />

      {(
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "clamp(28px, 4vw, 56px)", alignItems: "start", maxWidth: CAREERS_MAX_W, margin: "0 auto", padding: `clamp(28px, 5vw, 64px) ${INSET} clamp(48px, 7vw, 88px)` }}>
          {/* LEFT: intro + form */}
          <main style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)", minWidth: 0, animation: "ci-rise 460ms var(--ease-out) both" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", alignItems: "flex-start" }}>
              <span style={{ display: "inline-flex", alignItems: "center", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--accent-text)", border: "1px solid var(--accent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)", borderRadius: "var(--radius-pill)", padding: "5px 10px" }}>Step 1 of 2 · Free, no account</span>
              <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(32px, 4.4vw, 52px)", lineHeight: 1.05, letterSpacing: "-0.035em", textWrap: "pretty" }}>
                Find the internships you&apos;re <span style={{ color: "var(--accent-text)" }}>actually eligible</span> for.
              </h1>
              <p style={{ margin: 0, maxWidth: "52ch", fontSize: "clamp(15px, 1.3vw, 18px)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)", textWrap: "pretty" }}>
                Three things and you&apos;re in, no account needed to look. See real internships that fit, eligibility-checked against each posting&apos;s actual requirements.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--status-success-fill)", animation: "ci-pulse 2.4s ease-in-out infinite" }} />
                <span>{coverage ? `${coverage.toLocaleString("en-US")} internships tracked · refreshed daily` : "Refreshed daily"}</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)", background: "var(--surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-xl)", padding: "clamp(20px, 3vw, 32px)", boxShadow: "var(--elevation-2, 0 8px 24px -12px rgba(0,0,0,0.4))" }}>
              {/* résumé autofill callout */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "var(--space-3)", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", borderLeft: "3px solid var(--status-info-fill)" }}>
                <input ref={fileInput} type="file" accept=".pdf,.docx,.txt,.md" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadResume(f) }} />
                <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", minWidth: 0 }}>
                  <span style={{ color: "var(--status-info-text)", fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>↑</span>
                  <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)", lineHeight: 1.5, minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 600, color: "var(--text-primary)" }}>Have a résumé? Autofill this form</span>
                    We read the text to pre-fill your year, roles, and cities. Nothing is saved.
                    {!resumeBusy && !parsed && !resumeError && resumeNote && <span style={{ display: "block", color: "var(--status-success-text)", marginTop: 4 }}>{resumeNote}</span>}
                  </span>
                </div>
                {/* Three ways in: upload a file, paste the text, or link to it — all land on the same parse. */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {([["file", "Upload file"], ["paste", "Paste text"], ["link", "Paste a link"]] as const).map(([m, label]) => (
                    <button key={m} onClick={() => { setIntakeMode(m); setResumeError(null) }} disabled={resumeBusy} style={{
                      fontSize: "var(--type-xs)", fontWeight: 600, padding: "6px 12px", borderRadius: "var(--radius-pill)", cursor: resumeBusy ? "wait" : "pointer",
                      color: intakeMode === m ? "var(--accent-text)" : "var(--text-muted)",
                      background: intakeMode === m ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "transparent",
                      border: intakeMode === m ? "1px solid var(--accent)" : "1px solid var(--border-strong)",
                    }}>{label}</button>
                  ))}
                </div>
                {intakeMode === "file" && (
                  <button onClick={() => fileInput.current?.click()} disabled={resumeBusy} style={{ width: "100%", minHeight: 46, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "transparent", color: "var(--text-primary)", cursor: resumeBusy ? "wait" : "pointer" }}>
                    {resumeBusy ? "Reading…" : parsed ? "Upload a different file" : "Upload résumé"}
                  </button>
                )}
                {intakeMode === "paste" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} disabled={resumeBusy} rows={7}
                      placeholder="Paste your résumé text here…"
                      style={{ width: "100%", minHeight: 150, resize: "vertical", padding: "10px 12px", fontSize: "var(--type-sm)", fontFamily: "inherit", lineHeight: 1.5, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text-primary)" }} />
                    <button onClick={submitResumeText} disabled={resumeBusy || pasteText.trim().length < 40} style={{ minHeight: 44, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "transparent", color: "var(--text-primary)", cursor: resumeBusy || pasteText.trim().length < 40 ? "default" : "pointer", opacity: resumeBusy || pasteText.trim().length < 40 ? 0.6 : 1 }}>
                      {resumeBusy ? "Reading…" : "Use this résumé"}
                    </button>
                  </div>
                )}
                {intakeMode === "link" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} disabled={resumeBusy}
                      placeholder="https://…/your-resume.pdf"
                      style={{ width: "100%", minHeight: 46, padding: "0 12px", fontSize: "var(--type-sm)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text-primary)" }} />
                    <button onClick={submitResumeLink} disabled={resumeBusy || !linkUrl.trim()} style={{ minHeight: 44, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "transparent", color: "var(--text-primary)", cursor: resumeBusy || !linkUrl.trim() ? "default" : "pointer", opacity: resumeBusy || !linkUrl.trim() ? 0.6 : 1 }}>
                      {resumeBusy ? "Fetching…" : "Import from link"}
                    </button>
                    <span style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)" }}>A direct, public link to a PDF, DOCX or TXT.</span>
                  </div>
                )}
              </div>

              {/* processing steps (while parsing) */}
              {resumeBusy && (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
                  {["Reading your résumé", "Extracting your details"].map((label, i) => {
                    const done = resumeStage > i, active = resumeStage === i
                    return (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", fontSize: "var(--type-sm)", color: done || active ? "var(--text-primary)" : "var(--text-muted)" }}>
                        <span style={{ width: 9, height: 9, borderRadius: 999, flexShrink: 0, background: done ? "var(--status-success-fill)" : active ? "var(--accent)" : "var(--border-strong)", animation: active ? "ci-pulse 1.2s ease-in-out infinite" : undefined }} />
                        {done ? `${label} ✓` : `${label}…`}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* parse failed (friendly, in-card, never the raw library error) */}
              {!resumeBusy && resumeError && (
                <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", background: "color-mix(in srgb, var(--status-warning-fill) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--status-warning-fill) 34%, transparent)", fontSize: "var(--type-sm)", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                  <span style={{ color: "var(--status-warning-text)", flexShrink: 0 }}>△</span>
                  <span>{resumeError}</span>
                </div>
              )}

              {/* review card: confirm the parse before it fills the form */}
              {!resumeBusy && parsed && (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", padding: "var(--space-5)", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", border: "1px solid var(--accent)" }}>
                  <div style={{ fontSize: "var(--type-sm)", fontWeight: 700, color: "var(--text-primary)" }}>Here&apos;s what we read from your résumé</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {[
                      ["Graduates", parsed.graduation_year != null ? `Class of ${parsed.graduation_year}` : "not found"],
                      ["Roles", parsed.titles?.length ? parsed.titles.join(", ") : "any"],
                      ["Cities", parsed.locations?.length ? parsed.locations.join(", ") : "any"],
                      ...([parsed.major, parsed.university].filter(Boolean).length ? [["Major / school", [parsed.major, parsed.university].filter(Boolean).join(" · ")] as [string, string]] : []),
                      ...(parsed.skills?.length ? [["Skills", `${parsed.skills.length} detected`] as [string, string]] : []),
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: "flex", gap: "var(--space-3)", fontSize: "var(--type-sm)", minWidth: 0 }}>
                        <span style={{ flex: "0 0 96px", color: "var(--text-muted)", fontWeight: 600 }}>{k}</span>
                        <span style={{ color: "var(--text-primary)", minWidth: 0, wordBreak: "break-word" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center" }}>
                    <button
                      onClick={() => { const p = parsed; applyParsed(p); setParsed(null); setResumeNote("Applied from your résumé."); void trackCareer("tester_resume_confirmed", { action: "show_matches" }); setAutoSubmit(true) }}
                      style={{ minHeight: 44, padding: "0 18px", fontSize: 15, fontWeight: 700, borderRadius: "var(--radius-md)", border: "none", cursor: "pointer", background: "var(--accent)", color: "var(--on-accent, #fff)" }}
                    >
                      Looks right, show my internships
                    </button>
                    <button
                      onClick={() => { const p = parsed; applyParsed(p); setParsed(null); const filled = [p.university, p.major].filter(Boolean).join(" · "); setResumeNote(filled ? `Filled from your résumé: ${filled}. Adjust anything below.` : "Filled from your résumé. Adjust anything below."); void trackCareer("tester_resume_confirmed", { action: "edit" }) }}
                      style={{ minHeight: 44, padding: "0 16px", fontSize: 15, fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "transparent", color: "var(--text-primary)", cursor: "pointer" }}
                    >
                      Edit first
                    </button>
                  </div>
                  <div style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)" }}>Nothing is saved. You can change any of this below.</div>
                </div>
              )}

              {/* grad year (required, default 2028: the modal junior for the Summer 2027 season) */}
              <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                <legend style={{ padding: 0, fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>When do you graduate?</legend>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))", gap: "var(--space-2)" }}>
                  {GRAD_YEARS.map((y) => (
                    <button key={y} type="button" onClick={() => pickGradYear(y)} style={yearBtn(gradYear === y)}>{y}</button>
                  ))}
                </div>
              </fieldset>

              {/* target roles / titles */}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
                <span style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Target roles / titles{optionalTag}</span>
                <div style={{ boxSizing: "border-box", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-2)", minHeight: 52, padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", border: "1px solid var(--border-strong)", borderRadius: "var(--radius-md)" }}>
                  {titles.map((t) => (
                    <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, minHeight: 28, fontSize: "var(--type-xs)", fontWeight: 600, padding: "0 6px 0 10px", borderRadius: "var(--radius-pill)", color: "var(--accent-text)", background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 35%, transparent)" }}>
                      {t}
                      <button onClick={() => setTitles((cur) => cur.filter((x) => x !== t))} aria-label={`Remove ${t}`} style={{ appearance: "none", background: "none", border: 0, padding: "0 0 0 2px", color: "inherit", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                  <input list="dl-titles" autoComplete="off" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} onKeyDown={onTitleKey} onBlur={() => titleDraft.trim() && addTitle(titleDraft)} placeholder="Type or pick a title, any industry" style={{ flex: 1, minWidth: 140, appearance: "none", background: "none", border: 0, outline: "none", color: "var(--text-primary)", font: "inherit", fontSize: "var(--type-base)", padding: "var(--space-2) 0" }} />
                  <datalist id="dl-titles">{ROLE_TITLES.map((t) => <option key={t} value={t} />)}</datalist>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", marginTop: "var(--space-1)" }}>
                  {TITLE_SUGGESTIONS.map((s) => {
                    const on = titles.includes(s)
                    return (
                      <button key={s} type="button" onClick={() => (on ? setTitles((cur) => cur.filter((x) => x !== s)) : addTitle(s))} style={{ minHeight: 44, padding: "0 14px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-pill)", cursor: "pointer", color: on ? "var(--accent-text)" : "var(--text-secondary)", background: on ? "color-mix(in srgb, var(--accent) 12%, transparent)" : "transparent", border: `1px solid ${on ? "color-mix(in srgb, var(--accent) 35%, transparent)" : "var(--border-subtle)"}` }}>
                        {on ? "✓ " : "+ "}{s}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* industries */}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
                <span style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Industries{optionalTag}</span>
                <MultiSelect options={INDUSTRIES} value={industries} onChange={(v) => { markStarted(); setIndustries(v) }} placeholder="Add industries…" confirm />
              </div>

              {/* locations + term */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--space-4)" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
                  <span style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Preferred locations{optionalTag}</span>
                  <MultiSelect options={LOCATIONS} value={locations} onChange={setLocations} placeholder="Add cities…" confirm />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
                  <span style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Internship term{optionalTag}</span>
                  <select style={field} value={term} onChange={(e) => setTerm(e.target.value)}>
                    {TERMS.map((t) => <option key={t.v || "any"} value={t.v}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {error && <div style={{ fontSize: "var(--type-sm)", color: "var(--status-danger-text)" }}>{error}</div>}

              {/* Desktop submit lives inline in the form (no floating bottom bar on desktop);
                  mobile keeps the thumb-reachable sticky bar below. Same handler + guards. */}
              <DesktopOnly>
                <button onClick={submit} disabled={!canSubmit || busy} style={{ width: "100%", minHeight: 50, padding: "0 20px", fontSize: 16, fontWeight: 700, borderRadius: "var(--radius-md)", border: "none", cursor: canSubmit && !busy ? "pointer" : "not-allowed", background: canSubmit && !busy ? "var(--accent)" : "var(--border-subtle)", color: canSubmit && !busy ? "var(--on-accent, #fff)" : "var(--text-muted)" }}>
                  {busy ? "Finding your matches…" : "Show my internships"}
                </button>
              </DesktopOnly>

              {/* The submit CTA lives in the sticky bottom bar on mobile (always thumb-reachable);
                  this is just the standing promise. */}
              <p style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)", lineHeight: 1.7 }}>
                Free. No account or email needed to look.<br />Create an account for the full ranked feed and daily alerts, free for {pricing.trialDays} days with no card, then {pricing.effective.label}/{pricing.effective.interval}{pricing.promoActive ? ` (${pricing.promoName}, normally ${pricing.standard.label})` : ""}.
              </p>
            </div>
          </main>

          {/* RIGHT: what you'll see next */}
          <aside style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", minWidth: 0, animation: "ci-rise 600ms var(--ease-out) both" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>What you&apos;ll see next</span>
            <div style={{ overflow: "hidden", borderRadius: "var(--radius-xl)", background: "var(--surface)", border: "1px solid var(--border-subtle)", boxShadow: "var(--elevation-3, 0 24px 60px -20px rgba(0,0,0,0.5))" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", padding: "var(--space-4) var(--space-5)", background: "var(--surface-sunken)", borderBottom: "1px solid var(--border-subtle)" }}>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>Sample ranked feed</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)" }}>CS · {gradYear || 2028}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-4) var(--space-5) var(--space-5)" }}>
                <div style={{ display: "flex", gap: "var(--space-4)", alignItems: "flex-start", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", border: "1px solid var(--accent)", background: "color-mix(in srgb, var(--accent) 8%, var(--surface))" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>SWE Intern, Platform</span>
                    <span style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)" }}>Northvale · Remote · Summer 2027</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--type-2xs)", fontWeight: 600, padding: "2px 8px", borderRadius: "var(--radius-pill)", border: "1px solid var(--status-success-border, #4FD08A)", color: "var(--status-success-text)" }}>✓ Class year</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--type-2xs)", fontWeight: 600, padding: "2px 8px", borderRadius: "var(--radius-pill)", border: "1px solid var(--status-success-border, #4FD08A)", color: "var(--status-success-text)" }}>✓ Major</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <span className="smx-badge smx-soft t-success" style={{ whiteSpace: "nowrap", fontSize: 10 }}>Strong match</span>
                    <Donut pct={94} size={56} label="fit" />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "var(--space-4)", alignItems: "flex-start", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>Data Analytics Intern</span>
                    <span style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)" }}>Halcyon Labs · New York, NY</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--type-2xs)", fontWeight: 600, padding: "2px 8px", borderRadius: "var(--radius-pill)", border: "1px solid var(--status-success-border, #4FD08A)", color: "var(--status-success-text)" }}>✓ Class year</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--type-2xs)", fontWeight: 600, padding: "2px 8px", borderRadius: "var(--radius-pill)", border: "1px solid color-mix(in srgb, var(--status-warning-fill) 45%, transparent)", color: "var(--status-warning-text)" }}>△ 1 skill gap</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <span className="smx-badge smx-soft t-warning" style={{ whiteSpace: "nowrap", fontSize: 10 }}>Worth a look</span>
                    <Donut pct={81} size={56} label="fit" />
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-lg)", border: "1px dashed var(--border-subtle)", opacity: 0.75 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--status-danger-text)" }}>Filtered out: requires 2026 graduates</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-5)", borderRadius: "var(--radius-xl)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Then, if you want it</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                {["Your full ranked feed, refreshed daily", "Email alerts when new matches post", "Score breakdowns and gap closers"].map((t) => (
                  <div key={t} style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", fontSize: "var(--type-sm)", color: "var(--text-secondary)" }}>
                    <span style={{ color: "var(--status-success-text)", fontWeight: 700 }}>✓</span>{t}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", paddingTop: "var(--space-2)", flexWrap: "wrap" }}>
                {pricing.promoActive && <span style={{ fontSize: "var(--type-sm)", color: "var(--text-muted)", textDecoration: "line-through" }}>{pricing.standard.label}</span>}
                <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-2xl)", fontWeight: 600, letterSpacing: "-0.02em" }}>{pricing.effective.label}</span>
                <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)" }}>/ {pricing.effective.interval} · free {pricing.trialDays}-day trial</span>
                <LaunchBadge pricing={pricing} />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Shared public-site footer (consistent across the whole careers site) */}
      <CareersFooter />

      {/* Sticky bottom action bar (MOBILE ONLY): the primary CTA, thumb-reachable, with a live
          summary of what is entered. Hidden on desktop, where the submit is inline in the form. */}
      <div className={MKT_MOBILE_ONLY}>
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "12px clamp(16px, 4vw, 24px) max(12px, env(safe-area-inset-bottom))", background: "color-mix(in srgb, var(--surface-raised, var(--surface)) 94%, transparent)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderTop: "1px solid var(--border-subtle)" }}>
          <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {busy ? "Finding your matches…" : titles.length ? `${gradYear || 2028} · ${titles.length} ${titles.length === 1 ? "role" : "roles"}` : `Graduating ${gradYear || 2028}`}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>
              {titles.length ? "Ready to search" : "Add a role, or just search"}
            </span>
          </span>
          <button onClick={submit} disabled={!canSubmit || busy} style={{ flexShrink: 0, minHeight: 48, padding: "0 20px", fontSize: 16, fontWeight: 700, borderRadius: "var(--radius-md)", border: "none", cursor: canSubmit && !busy ? "pointer" : "not-allowed", background: canSubmit && !busy ? "var(--accent)" : "var(--border-subtle)", color: canSubmit && !busy ? "var(--on-accent, #fff)" : "var(--text-muted)" }}>
            {busy ? "Finding…" : "Show my internships"}
          </button>
        </div>
      </div>
    </div>
  )
}
